/**
 * IDs branded do domínio Planuze.
 *
 * Cuid v2 é o gerador padrão: colisão-resistente, ordenável (k-ordered),
 * URL-safe e sem PII.
 *
 * Convenção: cada entidade do domínio tem seu próprio brand. ID de coleção
 * NÃO é intercambiável com ID de projeto, mesmo que ambos sejam strings.
 */
import { createId as cuid } from '@paralleldrive/cuid2';
import type { Brand } from '@planuze/core-kit';

/* ============== Domain entity IDs (manifest + projeto do usuário) ============== */
export type ProjectId = Brand<string, 'ProjectId'>;
export type ModuleId = Brand<string, 'ModuleId'>;
export type CollectionId = Brand<string, 'CollectionId'>;
export type ColumnId = Brand<string, 'ColumnId'>;
export type RelationId = Brand<string, 'RelationId'>;
export type RouteId = Brand<string, 'RouteId'>;
export type ValidatorId = Brand<string, 'ValidatorId'>;
export type CrossRelationId = Brand<string, 'CrossRelationId'>;

/* Pack system IDs (PackId/PackVersion/PackSignature) vivem em @planuze/core-kit
 * (`types/pack-ids.ts`) — vocabulário do SDK público (ADR-0429). Re-exportados por
 * `@planuze/kernel` via `export * from '@planuze/core-kit'`. */

/* ============== User / Auth / Session IDs ============== */
export type UserId = Brand<string, 'UserId'>;
export type DeviceId = Brand<string, 'DeviceId'>;
export type LicenseTokenId = Brand<string, 'LicenseTokenId'>;
export type SessionId = Brand<string, 'SessionId'>;
export type MessageId = Brand<string, 'MessageId'>;

/* ============== Factories — criam IDs novos via cuid v2 ============== */
export const createProjectId = (): ProjectId => cuid() as ProjectId;
export const createModuleId = (): ModuleId => cuid() as ModuleId;
export const createCollectionId = (): CollectionId => cuid() as CollectionId;
export const createColumnId = (): ColumnId => cuid() as ColumnId;
export const createRelationId = (): RelationId => cuid() as RelationId;
export const createRouteId = (): RouteId => cuid() as RouteId;
export const createValidatorId = (): ValidatorId => cuid() as ValidatorId;
export const createCrossRelationId = (): CrossRelationId => cuid() as CrossRelationId;
export const createDeviceId = (): DeviceId => cuid() as DeviceId;
export const createSessionId = (): SessionId => cuid() as SessionId;
export const createMessageId = (): MessageId => cuid() as MessageId;

/* ============== Type guards ============== */
/**
 * Valida formato de cuid v2: lowercase alfanumérico, ≥24 chars.
 * Não valida que o ID é genuinamente único — apenas a forma.
 */
export const isCuid = (s: unknown): s is string =>
  typeof s === 'string' && /^[a-z0-9]{24,}$/.test(s);
